import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const lotTicketKeys = {
  all: ['lot_tickets'],
  lists: () => [...lotTicketKeys.all, 'list'],
  details: () => [...lotTicketKeys.all, 'detail'],
  detail: (id) => [...lotTicketKeys.details(), id],
};

export const useLotTickets = () => {
  return useQuery({
    queryKey: lotTicketKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('lot_tickets');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useLotTicket = (id, options = {}) => {
  return useQuery({
    queryKey: lotTicketKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`lot_tickets/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useCreateLotTicket = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (lotTicketData) => {
      const response = await apiClient.post('lot_tickets', lotTicketData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lotTicketKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['lots'] });
    },
    onError: (error) => {
      console.error('Error creating lot ticket:', error);
    },
  });
};

export const useUpdateLotTicket = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, lotTicketData }) => {
      const response = await apiClient.put(`lot_tickets/${id}`, lotTicketData);
      return response.data;
    },
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: lotTicketKeys.lists() });
      queryClient.invalidateQueries({ queryKey: lotTicketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ['lots'] });
    },
    onError: (error) => {
      console.error('Error updating lot ticket:', error);
    },
  });
};

export const useDeleteLotTicket = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`lot_tickets/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: lotTicketKeys.lists() });
      queryClient.removeQueries({ queryKey: lotTicketKeys.detail(deletedId) });
      queryClient.invalidateQueries({ queryKey: ['lots'] });
    },
    onError: (error) => {
      console.error('Error deleting lot ticket:', error);
    },
  });
};
